import React from 'react';
import {
    TripIcon, DietIcon, NewsIcon, PromotionIcon,
    ExplainIcon, RefactorIcon, ErrorIcon, OptimizeIcon,
    SummaryIcon, FactCheckIcon, FillIcon, WhatIsIcon
} from "../SuggestionIcons";
import { useAppStore } from "../../../GlobalStore/GlobalStore";

const environmentPrompts = {
    web: [
        { text: "Healthy workout snacks", icon: TripIcon },
        { text: "How many r's in Strawberry", icon: DietIcon },
        { text: "What is an LLM?", icon: NewsIcon },
        { text: "Help me get a promotion", icon: PromotionIcon }
    ],
    mobile: [
        { text: "Healthy workout snacks", icon: TripIcon },
        { text: "How many r's in Strawberry", icon: DietIcon },
        { text: "What is an LLM?", icon: NewsIcon },
        { text: "Help me get a promotion", icon: PromotionIcon }
    ],
    ide: [
        { text: "Explain this code", icon: ExplainIcon },
        { text: "Fix the errors", icon: ErrorIcon },
        { text: "Optimize my code", icon: OptimizeIcon },
        { text: "Refactor this file", icon: RefactorIcon }
    ],
    chrome: [
        { text: "Give a summary", icon: SummaryIcon },
        { text: "Fact check this", icon: FactCheckIcon },
        { text: "Fill this in", icon: FillIcon },
        { text: "What is this", icon: WhatIsIcon }
    ]
};

const sizeConfig = {
    default: {
        scale: '',
        containerClass: 'max-w-xl md:max-w-4xl px-3',
        buttonClass: 'p-2 px-3.5 md:px-5',
        textClass: 'text-sm'
    },
    vscode: {
        scale: 'scale-90',
        containerClass: 'max-w-xl md:max-w-2xl px-0',
        buttonClass: 'p-1.5 px-2 md:px-3',
        textClass: 'text-sm'
    }
};

interface SuggestionsProps {
    onPromptClick: (prompt: { text: string; icon: React.ComponentType }) => void;
}

const Suggestions: React.FC<SuggestionsProps> = ({ onPromptClick }) => {
    const environment = useAppStore((state: any) => state.environmentObj.environment);
    const dynamicStateObj = useAppStore((state: any) => state.dynamicStateObj);
    const { hasText, hasAttachment, isInitializing } = dynamicStateObj;

    const getPrompts = () => {
        if (environment.includes('jetbrains') || environment === 'vscode') {
            return environmentPrompts.ide;
        } else if (environment === 'chrome') {
            return environmentPrompts.chrome;
        } else if (environment.includes('mobile')) {
            return environmentPrompts.mobile;
        } else {
            return environmentPrompts.web;
        }
    };

    const prompts = getPrompts();
    const size = environment === 'vscode' ? sizeConfig.vscode : sizeConfig.default;

    const renderPrompts = () => {
        return prompts.map((prompt, index) => {
            const Icon = prompt.icon;
            return (
                <button
                    key={index}
                    onClick={() => onPromptClick(prompt)}
                    disabled={isInitializing}
                    className={`${size.buttonClass} ${isInitializing ? 'opacity-50 cursor-default' : ''} 
                        bg-neutral-200/50 dark:bg-neutral-950/30 rounded-xl 
                        hover:bg-neutral-300/50 dark:hover:bg-neutral-800/30 
                        transition-colors duration-200 border border-neutral-300/50 
                        dark:border-neutral-700/50 flex flex-col items-center 
                        justify-center gap-2 flex-shrink-0 mx-1.5`}
                    style={{ minWidth: '150px' }}
                >
                    <Icon />
                    <span className={`${size.textClass} leading-tight text-center`}>{prompt.text}</span>
                </button>
            );
        });
    };

    return (
        <div className={`w-full transition-opacity duration-300 
            ${hasText || hasAttachment ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}>
            <div className={`w-full ${size.scale} ${size.containerClass} mx-auto group`}>
                <div className="flex overflow-x-auto gap-1 pb-2 pr-2 [&::-webkit-scrollbar]:h-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-transparent group-hover:[&::-webkit-scrollbar-thumb]:bg-black/20 dark:group-hover:[&::-webkit-scrollbar-thumb]:bg-white/20 [&::-webkit-scrollbar-thumb]:transition-[background-color] [&::-webkit-scrollbar-thumb]:duration-500">
                    {renderPrompts()}
                </div>
            </div>
        </div>
    );
};

export default Suggestions;